import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowDown, CloudOff, Layers2, RefreshCw } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { prettyModel } from '../lib/format.js';
import { defaultLevel as defaultLevelFor, effectiveState, openAsks, turns as turnsOf, } from '../model.js';
import { runStateInput, runStateModel, syncingModel, } from '../run-state.js';
import { Composer } from './Composer.js';
import { Marker } from './Marker.js';
import { OlderTurnsButton } from './OlderTurns.js';
import { ProgressView } from './ProgressView.js';
import { AGENT_NAME, cx, useNow, useViewer, ViewerContext, } from './primitives.js';
import { RefInspector } from './RefInspector.js';
import { SessionHeader } from './SessionHeader.js';
import { TodoBar } from './TodoBar.js';
import { TurnGroup } from './TurnGroup.js';
import { UsageLimitPanel } from './UsageLimitPanel.js';
/** The one run state shared by the header badge, progress verdict and live "now" row. */
export function useRunState(view, { sync, lifecycle, cursor, now: frozen, } = {}) {
    const ticking = useNow(frozen == null, 15000);
    const now = frozen ?? ticking;
    const syncing = !!sync?.syncing || view.connection === 'reconnecting';
    const held = sync?.syncing ? sync.held : undefined;
    // biome-ignore lint/correctness/useExhaustiveDependencies: derived from the view's reducer outputs
    const live = useMemo(() => runStateInput(view), [
        view.progress,
        view.status,
        view.awaiting,
        view.entries,
        view.activity.lastEventAt,
        view.material,
    ]);
    const input = held ?? live;
    const o = {
        now,
        offline: view.connection === 'offline',
        syncing,
        ...(cursor != null ? { cursor } : {}),
        ...lifecycle,
    };
    const model = sync?.syncing && !held && view.connection !== 'offline'
        ? syncingModel(input, o)
        : runStateModel(input, o);
    return { ...model, input };
}
export function runState(view) {
    if (view.connection !== 'live')
        return 'stale';
    if (view.awaiting)
        return 'awaiting-user';
    if (view.status === 'error')
        return 'error';
    if (view.status === 'running' || view.status === 'compacting')
        return 'streaming';
    const last = turnsOf(view).at(-1);
    if (last && !last.outcome)
        return 'streaming';
    return last?.state === 'error' ? 'error' : 'ok';
}
const LEVELS = [
    { level: 0, label: 'Summary' },
    { level: 1, label: 'Activity' },
    { level: 2, label: 'Full' },
];
/**
 * The conversation viewer: header, todo bar, timeline of turns/markers with bottom anchoring
 * ("following"), jump-to-latest, offline banner, polite live announcements, and the composer.
 */
export function Transcript({ view, header, composer, handlers, level: globalLevel, showLevelControl = true, context, className, initialScroll = 'bottom', autoFocusAsk = true, defaultView = 'transcript', progressCursor = 0, sync, lifecycle, usageLimit, older, }) {
    const [mode, setMode] = useState(defaultView);
    const scroller = useRef(null);
    const [following, setFollowing] = useState(defaultView !== 'progress' && initialScroll === 'bottom');
    const [level, setLevel] = useState(globalLevel);
    // Jump target from the progress view's activity feed: open its turn and scroll to the item.
    const [focus, setFocus] = useState(null);
    useLayoutEffect(() => {
        const el = scroller.current;
        if (!focus || mode !== 'transcript' || !el)
            return;
        const item = Array.from(el.querySelectorAll('[data-item]')).find((n) => (n.dataset.item ?? '').split(' ').includes(focus.itemId));
        const target = item ??
            (focus.turnId
                ? Array.from(el.querySelectorAll('[data-turn-id]')).find((n) => n.dataset.turnId === focus.turnId)
                : undefined);
        target?.scrollIntoView?.({ block: 'center' });
        target?.classList.add('vk-flash');
    }, [focus, mode]);
    const [announce, setAnnounce] = useState('');
    const all = turnsOf(view);
    const latest = all.at(-1);
    const outer = useViewer();
    const ctx = {
        ...outer,
        connection: view.connection,
        cwd: view.session.cwd,
        ...context,
    };
    const rs = useRunState(view, {
        ...(sync ? { sync } : {}),
        ...(lifecycle ? { lifecycle } : {}),
        cursor: progressCursor,
        ...(ctx.now != null ? { now: ctx.now } : {}),
    });
    const state = rs.itemState;
    // Bottom anchoring: stay pinned to the end while following.
    // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on every new event
    useLayoutEffect(() => {
        const el = scroller.current;
        if (el && following)
            el.scrollTop = el.scrollHeight;
    }, [view.lastSeq, following]);
    // Polite announcements: turn completion and new questions — never per token.
    const prev = useRef({ turns: all.length, done: latest?.outcome, asks: view.awaiting });
    useEffect(() => {
        const p = prev.current;
        if (latest?.outcome && !p.done && p.turns === all.length) {
            setAnnounce(`${AGENT_NAME[view.agent]} finished turn ${latest.index}`);
        }
        else if (view.awaiting > p.asks) {
            const ask = openAsks(view).at(-1);
            setAnnounce(ask?.type === 'question'
                ? `${AGENT_NAME[view.agent]} asks: ${ask.questions[0]?.question ?? ''}`
                : `${AGENT_NAME[view.agent]} needs approval`);
        }
        prev.current = { turns: all.length, done: latest?.outcome, asks: view.awaiting };
    }, [latest?.outcome, view.awaiting, all.length, latest?.index, view]);
    // Prepended earlier turns keep the reader's place (the content above grew by the delta).
    const height = useRef({ base: view.turnBase ?? 0, scroll: 0 });
    // Runs after every render (no deps): measures the content height each time.
    useLayoutEffect(() => {
        const el = scroller.current;
        if (!el)
            return;
        const base = view.turnBase ?? 0;
        if (base < height.current.base && !following && mode === 'transcript')
            el.scrollTop += el.scrollHeight - height.current.scroll;
        height.current = { base, scroll: el.scrollHeight };
    });
    const onScroll = () => {
        const el = scroller.current;
        if (!el)
            return;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
        if (atBottom !== following)
            setFollowing(atBottom);
        if (mode === 'transcript' &&
            older &&
            older.turns > 0 &&
            !older.loading &&
            !older.error &&
            el.scrollTop < 160)
            older.onLoad();
    };
    const busy = rs.input.turnOpen || rs.input.awaiting > 0;
    return (_jsx(ViewerContext.Provider, { value: ctx, children: _jsxs("div", { className: cx('vk-viewer', mode === 'progress' && 'vk-viewer--progress', className), children: [header ? (_jsx(SessionHeader, { agent: view.agent, state: state, stateLabel: rs.label, stateReason: rs.reason, syncing: rs.syncing && rs.phase !== 'syncing', onStateClick: () => {
                        setFocus(null);
                        setFollowing(false);
                        setMode('progress');
                    }, model: view.session.model, contextPct: view.usage?.contextPct, contextTokens: view.usage?.contextTokens, contextWindow: view.usage?.contextWindow, costUsd: view.usage?.costUsd, ...(view.usage?.cumulative ? { contextBreakdown: view.usage.cumulative } : {}), compactions: view.entries.filter((x) => x.kind === 'interstitial' && x.item.type === 'compaction')
                        .length, ...header })) : null, view.connection !== 'live' ? (_jsxs("div", { className: cx('vk-conn', `vk-conn--${view.connection}`), role: "status", children: [view.connection === 'offline' ? (_jsx(CloudOff, { size: 14, "aria-hidden": "true" })) : (_jsx(RefreshCw, { size: 14, className: "vk-spin", "aria-hidden": "true" })), _jsx("span", { children: view.connection === 'offline'
                                ? 'Offline — showing the last synced state. Live items are paused.'
                                : `Reconnecting… resuming from event ${view.lastSeq}` })] })) : null, view.usageLimit && ['parked', 'resuming', 'failed'].includes(view.usageLimit.state) ? (_jsx(UsageLimitPanel, { park: view.usageLimit, ...(usageLimit ? { actions: usageLimit } : {}) })) : null, _jsx("div", { className: "vk-view-switch", role: "group", "aria-label": "Session view", children: ['progress', 'transcript'].map((m) => (_jsx("button", { type: "button", "aria-pressed": mode === m, onClick: () => {
                            setMode(m);
                            setFocus(null);
                            setFollowing(false);
                        }, children: m === 'progress' ? 'Progress' : 'Transcript' }, m))) }), mode === 'transcript' && view.todos?.length ? _jsx(TodoBar, { items: view.todos }) : null, _jsx("div", { className: "vk-viewer__scroll", ref: scroller, onScroll: onScroll, children: _jsx("div", { className: "vk-viewer__col", children: mode === 'progress' ? (_jsx(ProgressView, { view: view, state: rs, ...(header?.title ? { title: header.title } : {}), showTitle: !header, cursor: progressCursor, handlers: handlers, ...(older ? { older } : {}), onOpenItem: (t) => {
                                setFollowing(false);
                                setFocus(t);
                                setMode('transcript');
                            } })) : (_jsxs(_Fragment, { children: [showLevelControl ? (_jsxs("div", { className: "vk-levels", role: "group", "aria-label": "Detail level", children: [_jsx(Layers2, { size: 13, "aria-hidden": "true" }), LEVELS.map((l) => (_jsx("button", { type: "button", "aria-pressed": level === l.level, className: cx('vk-levels__btn', level === l.level && 'is-on'), onClick: () => setLevel(level === l.level ? undefined : l.level), children: l.label }, l.level)))] })) : null, older && older.turns > 0 ? _jsx(OlderTurnsButton, { older: older }) : null, view.entries.map((e) => e.kind === 'turn' ? (_jsx(TurnGroup, { turn: e.turn, level: level, defaultLevel: focus?.turnId === e.turn.id ? 1 : defaultLevelFor(e.turn, e.turn === latest), handlers: handlers, isLatest: e.turn === latest, autoFocusAsk: autoFocusAsk && e.turn === latest, onRetry: e.turn.outcome === 'error' ? () => { } : undefined, onFork: () => { } }, e.id)) : (_jsx("div", { className: "vk-interstitial", children: _jsx(Marker, { item: e.item }) }, e.id))), latest &&
                                    !latest.outcome &&
                                    effectiveState(latest.state, view.connection) === 'streaming' &&
                                    latest.items.length === 0 ? (_jsxs("div", { className: "vk-typing", "aria-hidden": "true", children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {})] })) : null] })) }) }), mode === 'transcript' && !following ? (_jsxs("button", { type: "button", className: "vk-jump", onClick: () => {
                        setFollowing(true);
                        const el = scroller.current;
                        if (el)
                            el.scrollTop = el.scrollHeight;
                    }, children: [_jsx(ArrowDown, { size: 14, "aria-hidden": "true" }), " Latest"] })) : null, composer !== false ? (_jsx(Composer, { agent: view.agent, busy: busy, model: view.session.model ? prettyModel(view.session.model) : undefined, effort: view.session.effort, disabled: view.connection === 'offline', queued: view.queued.map((q) => q.text), ...composer })) : null, _jsx("div", { className: "vk-sr", "aria-live": "polite", "aria-atomic": "true", children: announce }), ctx.refs ? _jsx(RefInspector, { store: ctx.refs }) : null] }) }));
}
//# sourceMappingURL=Transcript.js.map